import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Modal,
  Form,
  Select,
  DatePicker,
  Row,
  Col,
  Tag,
  message,
} from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import ChargingHttpHandler from '../../shared/chargingHttpHandler';
import secureStorage from '../../shared/secureStorage';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import isBetween from 'dayjs/plugin/isBetween';

// 設置 dayjs 本地化和插件
dayjs.locale('zh-tw');
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();
  const [memberInfo, setMemberInfo] = useState({}); // 儲存會員資料 {userId: {name, phone, account}}
  const [filterOptions, setFilterOptions] = useState({
    stationNames: [],
    businessNames: [],
    appTypes: [],
  });

  const generateFilterOptions = (orders) => {
    if (!orders || orders.length === 0) return;

    // 充電站名稱選項
    const stationNames = [
      ...new Set(orders.map((order) => order.evcs?.name).filter(Boolean)),
    ].map((stationName) => ({ label: stationName, value: stationName }));

    // 營運商名稱選項
    const businessNames = [
      ...new Set(orders.map((order) => order.businessName).filter(Boolean)),
    ].map((businessName) => ({ label: businessName, value: businessName }));

    // 應用商選項
    const appTypes = [
      ...new Set(
        orders.map((order) => order.chargeFeeRate?.app_type).filter(Boolean)
      ),
    ].map((appType) => ({ label: appType, value: appType }));

    setFilterOptions({
      stationNames,
      businessNames,
      appTypes,
    });
  };

  // 根據訂單中的 userId 獲取對應的會員資料~~~如果有出現未知 代表那是興聯那邊最一開始的測試資料 所以會員不是我們這邊的
  const fetchMemberInfoForOrders = async (orders) => {
    if (!orders || orders.length === 0) return;

    try {
      const httpHandler = new ChargingHttpHandler(false);
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');

      // 取得所有不重複的 userId
      const uniqueUserIds = [
        ...new Set(orders.map((order) => order.userId).filter(Boolean)),
      ];
      console.log('需要查詢的 userId 列表:', uniqueUserIds);

      if (uniqueUserIds.length === 0) return;

      // 一次性獲取所有會員資料
      const response = await httpHandler
        .axios()
        .get('/platform/get_member_info', {
          headers: { Authorization: `Bearer ${chargingAccessToken}` },
        });
      const memberMap = {};

      if (response.data && Array.isArray(response.data.data)) {
        response.data.data.forEach((member) => {
          if (member.account) {
            memberMap[member.account] = {
              name: member.name || '未知',
              phone: member.phone || '未知',
              carNumber: member.car_number || '未知',
              crCheck: member.cr_check || 0,
              account: member.account,
            };
          }
        });

        // 為訂單中的每個 userId 設定對應的會員資料
        uniqueUserIds.forEach((userId) => {
          if (!memberMap[userId]) {
            // 如果找不到對應的會員資料，設定預設值
            memberMap[userId] = {
              name: '未知',
              phone: '未知',
              carNumber: '未知',
              crCheck: 0,
              account: userId,
            };
          }
        });
      }

      setMemberInfo(memberMap);
    } catch (e) {
      console.error('獲取會員資料失敗', e);
    }
  };

  const add_search_log = async (success) => {
    const userRole = secureStorage.getItem('userRole');
    const httpHandler = new ChargingHttpHandler(false);
    const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
    // 紀錄搜尋時間
    await httpHandler
      .axios()
      .post(
        '/platform/update_user_log_table',
        {
          log_time: dayjs().format('YYYY/MM/DD HH:mm:ss'),
          user_role: userRole,
          action_type: '訂單搜尋',
          action_content: '用戶搜尋訂單資訊',
          status: success,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${chargingAccessToken}`,
          },
        }
      )
      .catch((e) => {
        console.error('寫入搜尋紀錄失敗', e);
      });
  };

  // 獲取訂單資料的部分
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const httpHandler = new ChargingHttpHandler(false);
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');

      const response = await httpHandler.axios().get('/platform/all-cdr-info', {
        headers: { Authorization: `Bearer ${chargingAccessToken}` },
      });

      if (response.data && Array.isArray(response.data.data)) {
        await add_search_log(1);
        setOrders(response.data.data);
        setFilteredOrders(response.data.data);

        generateFilterOptions(response.data.data);

        // 根據訂單中的 userId 查詢對應的會員資料
        await fetchMemberInfoForOrders(response.data.data);
      } else {
        await add_search_log(0);
      }
    } catch (error) {
      console.error('獲取訂單資料失敗', error);
      message.error('獲取訂單資料失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // 手機號碼隱碼：隱藏第5、6、7位
  const maskPhoneNumber = (phone) => {
    if (!phone || phone === '未知' || phone.length < 7) return phone;
    return phone.substring(0, 4) + '***' + phone.substring(7);
  };

  // 名字隱碼：只顯示第一個字，後面用*
  const maskName = (name) => {
    if (!name || name === '未知' || name.length === 0) return name;
    const firstChar = name.charAt(0);
    const maskedPart = '*'.repeat(name.length - 1);
    return firstChar + maskedPart;
  };

  const getStatusTag = (status) => {
    const statusMap = {
      無需付款: { text: '無需付款', color: 'default' },
      付款成功: { text: '付款成功', color: 'success' },
      結算中: { text: '結算中', color: 'processing' },
      付款失敗: { text: '付款失敗', color: 'error' },
      訂單作廢: { text: '訂單作廢', color: 'warning' },
      1: { text: '無需付款', color: 'default' },
      2: { text: '付款成功', color: 'success' },
      3: { text: '結算中', color: 'processing' },
      4: { text: '付款失敗', color: 'error' },
      5: { text: '訂單作廢', color: 'warning' },
    };
    const statusInfo = statusMap[status] || { text: status, color: 'default' };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  // 根據目前 filters 套用篩選（提供給按叉叉單獨清除用）
  const applyFilters = (values) => {
    let filtered = [...orders];

    if (values.orderNo && values.orderNo.trim()) {
      filtered = filtered.filter((order) =>
        order.orderNo?.toLowerCase().includes(values.orderNo.toLowerCase())
      );
    }

    if (values.status) {
      const statusMap = {
        1: '無需付款',
        2: '付款成功',
        3: '結算中',
        4: '付款失敗',
        5: '訂單作廢',
      };
      const filterStatusText = statusMap[values.status] || values.status;
      filtered = filtered.filter((order) => order.status === filterStatusText);
    }

    if (values.orderDate && values.orderDate.length === 2) {
      const [startDate, endDate] = values.orderDate;
      filtered = filtered.filter((order) => {
        const orderDate = dayjs(order.createdTime);
        return orderDate.isBetween(startDate, endDate, 'day', '[]');
      });
    }

    if (values.totalAmount && values.totalAmount.trim()) {
      filtered = filtered.filter(
        (order) =>
          order.totalAmount &&
          order.totalAmount.toString().includes(values.totalAmount)
      );
    }

    if (values.paymentDate && values.paymentDate.length === 2) {
      const [startDate, endDate] = values.paymentDate;
      const startTimestamp = startDate.clone().startOf('day').valueOf();
      const endTimestamp = endDate.clone().endOf('day').valueOf();
      filtered = filtered.filter((order) => {
        if (!order.paymentTime) return false;
        const paymentTimestamp = dayjs(order.paymentTime).valueOf();
        return (
          paymentTimestamp >= startTimestamp && paymentTimestamp <= endTimestamp
        );
      });
    }

    if (values.userId && values.userId.trim()) {
      filtered = filtered.filter((order) =>
        order.userId?.toLowerCase().includes(values.userId.toLowerCase())
      );
    }

    if (values.ownerName && values.ownerName.trim()) {
      filtered = filtered.filter((order) => {
        const member = memberInfo[order.userId];
        return member?.name
          ?.toLowerCase()
          .includes(values.ownerName.toLowerCase());
      });
    }

    if (values.phone && values.phone.trim()) {
      filtered = filtered.filter((order) => {
        const member = memberInfo[order.userId];
        return member?.phone?.includes(values.phone);
      });
    }

    if (values.carNumber && values.carNumber.trim()) {
      filtered = filtered.filter((order) => {
        const member = memberInfo[order.userId];
        return member?.carNumber
          ?.toLowerCase()
          .includes(values.carNumber.toLowerCase());
      });
    }

    if (values.crCheck !== undefined && values.crCheck !== '') {
      filtered = filtered.filter((order) => {
        const member = memberInfo[order.userId];
        return member?.crCheck === parseInt(values.crCheck);
      });
    }

    // 充電方式（已停用）
    // if (values.powerType) {
    //   filtered = filtered.filter(order => order.connector?.powerType === values.powerType);
    // }

    if (values.stationName && values.stationName.length > 0) {
      filtered = filtered.filter((order) =>
        values.stationName.some((station) =>
          order.evcs?.name?.toLowerCase().includes(station.toLowerCase())
        )
      );
    }

    if (values.businessName && values.businessName.length > 0) {
      filtered = filtered.filter((order) =>
        values.businessName.some((business) =>
          order.businessName?.toLowerCase().includes(business.toLowerCase())
        )
      );
    }

    if (values.appType) {
      filtered = filtered.filter(
        (order) => order.chargeFeeRate?.app_type === values.appType
      );
    }

    return filtered;
  };

  const handleRemoveFilter = (key) => {
    const next = { ...filters };
    if (Array.isArray(next[key])) {
      next[key] = [];
    } else {
      // 日期區間特別處理
      if (key === 'orderDate' || key === 'paymentDate') {
        next[key] = [];
      } else {
        delete next[key];
      }
    }
    setFilters(next);
    form.setFieldsValue({ [key]: Array.isArray(next[key]) ? [] : undefined });
    const result = applyFilters(next);
    setFilteredOrders(result);
  };

  // 表格欄位定義
  const columns = [
    {
      title: '訂單狀態',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
      filters: [
        { text: '無需付款', value: '無需付款' },
        { text: '付款成功', value: '付款成功' },
        { text: '結算中', value: '結算中' },
        { text: '付款失敗', value: '付款失敗' },
        { text: '訂單作廢', value: '訂單作廢' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: '訂單編號',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 150,
    },
    {
      title: '車主姓名',
      dataIndex: 'userId',
      key: 'ownerName',
      width: 120,
      render: (userId) => {
        const member = memberInfo[userId];
        const name = member?.name || '未知';
        return maskName(name);
      },
    },
    {
      title: '手機號碼',
      dataIndex: 'userId',
      key: 'phone',
      width: 130,
      render: (userId) => {
        const member = memberInfo[userId];
        const phone = member?.phone || '未知';
        return maskPhoneNumber(phone);
      },
    },
    {
      title: '使用者ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 150,
      render: (userId) => {
        const member = memberInfo[userId];
        return member?.account || userId || '未知';
      },
    },
    {
      title: '車牌號碼',
      dataIndex: 'userId',
      key: 'carNumber',
      width: 120,
      render: (userId) => {
        const member = memberInfo[userId];
        return member?.carNumber || '未知';
      },
    },
    {
      title: 'CR會員',
      dataIndex: 'userId',
      key: 'crCheck',
      width: 100,
      render: (userId) => {
        const member = memberInfo[userId];
        return member?.crCheck === 1 ? '是' : '否';
      },
    },
    {
      title: '訂單金額',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (amount) => `$${amount?.toFixed(2) || '0.00'}`,
    },
    {
      title: '充電站名稱',
      dataIndex: ['evcs', 'name'],
      key: 'stationName',
      width: 200,
    },
    // {
    //   title: '充電方式',
    //   dataIndex: ['connector', 'powerType'],
    //   key: 'powerType',
    //   width: 100,
    //   render: (powerType) => <Tag color={powerType === 'DC' ? 'blue' : 'green'}>{powerType}</Tag>,
    // },
    {
      title: '訂單建立時間',
      dataIndex: 'createdTime',
      key: 'createdTime',
      width: 180,
      render: (time) =>
        time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  // 搜尋功能
  const handleSearch = () => {
    if (!searchKeyword.trim()) {
      setFilteredOrders(orders);
      return;
    }

    const filtered = orders.filter(
      (order) =>
        order.orderNo?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        order.businessName
          ?.toLowerCase()
          .includes(searchKeyword.toLowerCase()) ||
        order.evcs?.name?.toLowerCase().includes(searchKeyword.toLowerCase())
    );
    setFilteredOrders(filtered);
  };

  // 進階篩選
  const handleAdvancedFilter = (values) => {
    let filtered = applyFilters(values);

    setFilteredOrders(filtered);
    setFilters(values);
    setFilterVisible(false);
    message.success(`篩選完成，共 ${filtered.length} 筆資料`);
  };

  // 清除篩選
  const clearFilters = () => {
    setFilteredOrders(orders);
    setFilters({});
    form.resetFields();
    setSearchKeyword('');
  };

  return (
    <div>
      {/* 搜尋和篩選區域 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="請輸入關鍵字"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 'calc(100% - 120px)' }}
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}>
                搜尋
              </Button>
            </Space.Compact>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterVisible(true)}>
                篩選
              </Button>
              <Button onClick={clearFilters}>清除</Button>
            </Space>
          </Col>
        </Row>

        {/* 篩選條件顯示 */}
        {Object.keys(filters).length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
            篩選條件:
            {Object.entries(filters)
              .filter(([key, value]) => {
                // 只顯示有實際值的篩選條件
                if (Array.isArray(value)) {
                  return value.length > 0;
                } else {
                  return value && value.toString().trim() !== '';
                }
              })
              .map(([key, value]) => {
                // 根據欄位名稱顯示對應的中文標籤~
                const fieldLabels = {
                  orderNo: '訂單編號',
                  status: '訂單狀態',
                  orderDate: '訂單日期',
                  totalAmount: '訂單金額',
                  paymentDate: '付款日期',
                  userId: '使用者ID',
                  ownerName: '車主姓名',
                  phone: '手機號碼',
                  carNumber: '車牌號碼',
                  crCheck: 'CR會員',
                  // powerType: '充電方式', // 已停用
                  stationName: '充電站名稱',
                  businessName: '營運商',
                  appType: '應用商',
                };

                let displayValue = '';

                if (Array.isArray(value)) {
                  if (key === 'orderDate' || key === 'paymentDate') {
                    displayValue = value
                      .map((v) => dayjs(v).format('YYYY-MM-DD'))
                      .join(' ~ ');
                  } else {
                    displayValue = value.join(', ');
                  }
                } else {
                  if (key === 'status') {
                    const statusMap = {
                      1: '無需付款',
                      2: '付款成功',
                      3: '結算中',
                      4: '付款失敗',
                      5: '訂單作廢',
                    };
                    displayValue = statusMap[value] || value;
                  } else if (key === 'appType') {
                    const appTypeMap = {
                      irent: 'iRent',
                      easyrent: 'EasyRent',
                    };
                    displayValue = appTypeMap[value] || value;
                  } else {
                    displayValue = value;
                  }
                }

                const fieldLabel = fieldLabels[key] || key;

                return (
                  <Tag
                    key={key}
                    closable
                    onClose={() => handleRemoveFilter(key)}>
                    {fieldLabel}
                  </Tag>
                );
              })}
          </div>
        )}
      </Card>

      {/* 資料表格 */}
      <Card>
        <div style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>
          共 {filteredOrders.length} 筆資料
        </div>

        <Table
          columns={columns}
          dataSource={filteredOrders}
          loading={loading}
          rowKey="orderNo"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: 1500 }}
        />
      </Card>

      {/* 進階篩選 Modal */}
      <Modal
        title="進階篩選"
        open={filterVisible}
        onCancel={() => setFilterVisible(false)}
        width={1000}
        footer={[
          <Button
            key="clear"
            onClick={() => {
              form.resetFields();
              setFilters({});
              setFilteredOrders(orders);
            }}>
            清除所有
          </Button>,
          <Button key="cancel" onClick={() => setFilterVisible(false)}>
            取消
          </Button>,
          <Button key="search" type="primary" onClick={() => form.submit()}>
            搜尋
          </Button>,
        ]}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAdvancedFilter}
          initialValues={filters}>
          {/* 訂單內容區塊 */}
          <div style={{ marginBottom: 24 }}>
            <h4 style={{ marginBottom: 16, fontSize: 16, fontWeight: 'bold' }}>
              訂單內容
            </h4>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="訂單編號" name="orderNo">
                  <Input placeholder="訂單編號" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="訂單狀態" name="status">
                  <Select placeholder="請選擇">
                    <Select.Option value="1">無需付款</Select.Option>
                    <Select.Option value="2">付款成功</Select.Option>
                    <Select.Option value="3">結算中</Select.Option>
                    <Select.Option value="4">付款失敗</Select.Option>
                    <Select.Option value="5">訂單作廢</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="訂單日期" name="orderDate">
                  <RangePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="訂單金額" name="totalAmount">
                  <Input placeholder="訂單金額" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="付款日期" name="paymentDate">
                  <RangePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="使用者ID" name="userId">
                  <Input placeholder="使用者ID" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="車主姓名" name="ownerName">
                  <Input placeholder="車主姓名" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="手機號碼" name="phone">
                  <Input placeholder="手機號碼" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="車牌號碼" name="carNumber">
                  <Input placeholder="車牌號碼" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="CR會員" name="crCheck">
                  <Select placeholder="請選擇">
                    <Select.Option value="1">是</Select.Option>
                    <Select.Option value="0">否</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              {/* <Col span={8}>
                <Form.Item label="充電方式" name="powerType">
                  <Select placeholder="請選擇">
                    <Select.Option value="AC">AC</Select.Option>
                    <Select.Option value="DC">DC</Select.Option>
                  </Select>
                </Form.Item>
              </Col> */}
              <Col span={8}>
                <Form.Item label="應用商" name="appType">
                  <Select placeholder="請選擇">
                    {filterOptions.appTypes.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* 充電站資訊區塊 */}
          <div>
            <h4 style={{ marginBottom: 16, fontSize: 16, fontWeight: 'bold' }}>
              充電站資訊
            </h4>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="充電站名稱" name="stationName">
                  <Select placeholder="請選擇" mode="multiple">
                    {filterOptions.stationNames.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="營運商" name="businessName">
                  <Select placeholder="請選擇" mode="multiple">
                    {filterOptions.businessNames.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderManagement;
